/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import ChatDrawer from '../../components/search-chat/chat';
import { useGetWidgetConfigQuery } from '../../store/widget/widgetConfigApi';
import { useGetCurrentUserQuery } from '../../store/user/userApi';
import { classNames } from '../../utils/classNames';

const WS_URL = process.env.REACT_APP_WS_URL;
const WS_PATH = process.env.REACT_APP_WS_PATH;
const SOCKET_CLIENT_ID = 'demo-client';
const DEFAULT_WIDGET_CLIENT_ID = 'd2957434-b0c1-7049-5d57-d6f3e5695d30';

type SocketInboundMessage = {
	messageId?: string;
	from?: string;
	text?: string;
	timestamp?: string;
};

const ChatPage: React.FC = () => {
	const [open, setOpen] = useState(false);
	const [showHint, setShowHint] = useState(false);
	const [socketMessages, setSocketMessages] = useState<any[]>([]);
	const location = useLocation();
	const navigate = useNavigate();
	const { slug } = useParams<{ slug?: string }>();
	const socketRef = useRef<Socket | null>(null);
	const joinedRef = useRef(false);
	const conversationIdRef = useRef('');
	const hasAccessToken =
		typeof window !== 'undefined' &&
		!!window.localStorage.getItem('accessToken');
	const { data: currentUser } = useGetCurrentUserQuery(undefined, {
		skip: !hasAccessToken,
	});
	const searchParams = new URLSearchParams(location.search);
	const scriptAgentId = searchParams.get('agentId') || 'leo';
	const scriptClientId = searchParams.get('clientId');
	const embedOnly = searchParams.get('embed') === '1';
	const widgetClientId =
		scriptClientId ||
		currentUser?.data?.companyData?.sub ||
		DEFAULT_WIDGET_CLIENT_ID;
	const { data: widgetConfig } = useGetWidgetConfigQuery(
		{ agentId: scriptAgentId, clientId: widgetClientId },
		{ skip: !widgetClientId },
	);

	const toggleAvatar = useMemo(() => {
		return `/assets/agents/${scriptAgentId.toLowerCase()}-agent.svg`;
	}, [widgetConfig]);
	const agentDisplayName = useMemo(() => {
		const raw = widgetConfig?.agent || scriptAgentId || 'leo';
		const normalized = String(raw).replace(/agent-|_/g, ' ').trim();
		return normalized
			? normalized.charAt(0).toUpperCase() + normalized.slice(1)
			: 'leo';
	}, [widgetConfig, scriptAgentId]);
	const popularDestinations = [
		{
			title: 'Matira Beach',
			subtitle: 'French Polynesia',
			image: '/images/moonlight/2c5f8a145d54c299c9c8041c32bb4beef66db081.jpg',
		},
		{
			title: 'Grace Bay Beach',
			subtitle: 'Turks and Caicos Islands',
			image: '/images/moonlight/d9a78715e8a49c5ab848d713fe995be8ccfc4c40.jpg',
		},
		{
			title: 'Miami Beach',
			subtitle: 'Florida',
			image: '/images/moonlight/2c5f8a145d54c299c9c8041c32bb4beef66db081.jpg',
		},
		{
			title: 'Navagio',
			subtitle: 'Greece',
			image: '/images/moonlight/b5a91bb9a1c1915e9596da829fbcec91af75eee3.jpg',
		},
	];
	const featuredYachts = [
		'/images/moonlight/4124196aea3574249261448d736d4fb3ed0978f5.jpg',
		'/images/moonlight/4ea47075546e58351ed833d8a9f1b65533ffbdff.jpg',
		'/images/moonlight/360faf46673db17497405261109efbe19e4586f7.jpg',
		'/images/moonlight/2dbd295e6f3781365dc77066e17dbeabe676a08d.jpg',
	];
	const memories = [
		'/images/moonlight/501dbdd6e332384b23347e11ce4cf31ac593a121.jpg',
		'/images/moonlight/0d4f06ec0e3d44ad88fc93f84f44b3f0534e809d.jpg',
		'/images/moonlight/c4b8566f667d9fa0ac39c07236a96e465fbad2fd.jpg',
	];
	const fadeUp = {
		initial: { opacity: 0, y: 18 },
		whileInView: { opacity: 1, y: 0 },
		viewport: { once: true, amount: 0.2 },
		transition: { duration: 0.45, ease: 'easeOut' as const },
	};
	const latestStories = [
		{
			slug: 'discover-manama-from-the-sea',
			tag: 'TRAVEL GUIDE',
			image: '/images/moonlight/4ea47075546e58351ed833d8a9f1b65533ffbdff.jpg',
			meta: 'March 27, 2026  •  8 min read',
			title: 'Discover Manama from the Sea: A Yacht Experience Near Ramada by Wyndham Manama City Centre',
			description:
				"There's a different side of Manama—one that reveals itself only when you leave the shore behind. Explore the city from a unique perspective aboard a luxury yacht.",
		},
		{
			slug: 'day-in-the-life-luxury-yacht-charter',
			tag: 'YACHT LIFE',
			image: '/images/moonlight/360faf46673db17497405261109efbe19e4586f7.jpg',
			meta: 'March 10, 2026  •  8 min read',
			title: 'A Day in the Life: Luxury Yacht Charter Experience',
			description:
				"Get an insider's look at what makes a luxury yacht charter truly unforgettable, from gourmet dining to water sports.",
		},
		{
			slug: 'hidden-gems-secret-beaches',
			tag: 'DESTINATIONS',
			image: '/images/moonlight/2dbd295e6f3781365dc77066e17dbeabe676a08d.jpg',
			meta: 'March 5, 2026  •  8 min read',
			title: 'Hidden Gems: Secret Beaches Only Accessible by Yacht',
			description:
				'Explore pristine, secluded beaches around the world that can only be reached by private yacht charter.',
		},
	];
	const selectedStory = useMemo(
		() => latestStories.find((story) => story.slug === slug) || null,
		[latestStories, slug],
	);
	const chatHintText = selectedStory
		? 'Hope you are having a good read, Would you like to check availability dates for Wyndham Manama?'
		: `Hi, I am ${agentDisplayName}, is there anything I can help you with?`;

	const toggleChat = () => {
		setOpen((prev) => !prev);
	};

	useEffect(() => {
		if (!open) return;

		const conversationStorageKey = 'widget_conversation_id';
		let localConversationId = localStorage.getItem(conversationStorageKey);
		if (!localConversationId) {
			localConversationId = `conv-${Date.now()}`;
			localStorage.setItem(conversationStorageKey, localConversationId);
		}
		conversationIdRef.current = localConversationId;
		joinedRef.current = false;

		const socket = io(WS_URL, {
			path: WS_PATH,
			transports: ['websocket'],
			autoConnect: false,
			auth: { clientId: SOCKET_CLIENT_ID, agentId: scriptAgentId },
		});

		const mapMessage = (msg: SocketInboundMessage) => {
			if (msg?.from === 'agent') {
				return {
					agent: {
						_id: msg.messageId,
						message: msg.text || '',
						createdAt: msg.timestamp || new Date().toISOString(),
						files: [],
					},
				};
			}
			return {
				user: {
					message: msg.text || '',
					createdAt: msg.timestamp || new Date().toISOString(),
					files: [],
				},
			};
		};

		const onMessageNew = (msg: SocketInboundMessage) => {
			// User messages are already appended optimistically in ChatDrawer.
			if (msg?.from !== 'agent') return;
			const normalized = mapMessage(msg);
			setSocketMessages((prev) => {
				const id = normalized?.agent?._id;
				if (id && prev.some((m: any) => m?.agent?._id === id)) return prev;
				return [...prev, normalized];
			});
		};

		const onConnect = async () => {
			try {
				const res = await socket.emitWithAck('conversation:join', {
					conversationId: localConversationId,
				});
				joinedRef.current = !!res?.ok;
			} catch (e) {
				console.error('socket join failed', e);
				joinedRef.current = false;
			}
		};

		const onDisconnect = () => {
			joinedRef.current = false;
		};

		const onSocketError = (payload: any) => {
			console.error('socket error:', payload);
		};

		socket.on('connect', onConnect);
		socket.on('disconnect', onDisconnect);
		socket.on('error', onSocketError);
		socket.on('message:new', onMessageNew);
		socket.connect();
		socketRef.current = socket;

		return () => {
			socket.off('connect', onConnect);
			socket.off('disconnect', onDisconnect);
			socket.off('error', onSocketError);
			socket.off('message:new', onMessageNew);
			socket.disconnect();
			socketRef.current = null;
			joinedRef.current = false;
		};
	}, [open, scriptAgentId]);

	const onSendSocketMessage = async (payload: { text: string }) => {
		const socket = socketRef.current;
		const conversationId = conversationIdRef.current;
		if (!payload?.text?.trim()) return;
		if (!socket || !socket.connected || !joinedRef.current || !conversationId) {
			throw new Error('Socket not connected yet. Please try again.');
		}
		const messageId = `m-${Date.now()}-${Math.random().toString(16).slice(2)}`;
		const ack = await socket.emitWithAck('message:send', {
			conversationId,
			messageId,
			from: 'user',
			text: payload.text.trim(),
			timestamp: new Date().toISOString(),
			metadata: { ui: 'widget' },
		});
		if (!ack?.ok) {
			throw new Error(ack?.error || 'Socket send failed');
		}
	};

	const openStoryInPage = (storySlug: string) => {
		navigate(`/chat/post/${storySlug}${location.search}`);
	};

	useEffect(() => {
		const timer = setTimeout(() => setShowHint(true), 2000);
		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		if (open) setShowHint(false);
	}, [open]);

	useEffect(() => {
		try {
			if (window.parent && window.parent !== window) {
				window.parent.postMessage(
					{ type: 'hotelworld-widget-toggle', open },
					'*',
				);
			}
		} catch (e) {
			// noop
		}
	}, [open]);

	return (
		<div
			className={classNames(
				'relative w-full text-[#0f172a]',
				embedOnly ? 'h-full bg-transparent' : 'min-h-screen bg-[#f5f5f5]',
			)}
		>
			{!embedOnly && (
				<div>
					{!selectedStory && (
						<>
							<motion.section
								className="relative h-screen overflow-hidden px-6 pt-6 text-white md:px-10"
								initial={{ opacity: 0, scale: 1.01 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ duration: 0.6, ease: 'easeOut' }}
								style={{
									backgroundImage:
										'linear-gradient(to bottom, rgba(0, 20, 25, 0.45), rgba(0, 20, 25, 0.55)), url("/images/moonlight/e54e091ba8e9b7c07e36e68d6631d71b4b63de64.jpg")',
									backgroundSize: 'cover',
									backgroundPosition: 'center',
								}}
							>
								<div className="flex items-center justify-between text-sm">
									<p className="font-light">
										<img src="/images/moonlight/logo.svg" alt="Moonlight" className="h-10" />
									</p>
									<nav className="gap-8 mx-auto text-sm hidden md:block">
										<div>Destinations</div>
										<div>Yachts</div>
										<div>Tours</div>
									</nav>
									<div className='flex items-center gap-6'>
										<button type="button" className="text-xs text-white">
											Contact
										</button>
										<button type="button" className="text-xs text-white">
											En
										</button>

									</div>
								</div>
								<div className="mt-20 md:mt-24">
									<p className="text-2xl tracking-[0.25em] font-normal">World Tour</p>
									<h1 className="mt-3 max-w-3xl text-4xl font-semibold tracking-[0.08em] md:text-6xl">
										RARE EXOTIC LOCATIONS
									</h1>
								</div>
								<motion.div
									initial={{ opacity: 0, y: 16 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.45, delay: 0.2, ease: 'easeOut' }}
									className="absolute bottom-[25%] left-6 right-6 rounded-md md:left-10 md:right-10 max-w-6xl mx-auto"
								>
									<div className="grid grid-cols-1 gap-3 md:grid-cols-4">
										<div className="rounded border border-white/95 px-3 py-5 text-sm text-white flex items-center">
											<img src="/assets/shared/destination.svg" alt="destination" className="w-5 h-5 mr-2" />
											Destination
										</div>
										<div className="rounded border border-white/95 px-3 py-5 text-sm text-white flex items-center">
											<img src="/assets/shared/date.svg" alt="date" className="w-5 h-5 mr-2" />
											Date
										</div>
										<div className="rounded border border-white/95 px-3 py-5 text-sm text-white flex items-center">
											<img src="/assets/shared/boat-type.svg" alt="boat type" className="w-5 h-5 mr-2" />
											Boat Type
										</div>
										<button className="rounded border bg-white/95 px-3 py-4 text-xl font-semibold text-black">
											Find Yachts/Tours
										</button>
									</div>
								</motion.div>
							</motion.section>

							<motion.section className="px-6 py-10 md:px-10" {...fadeUp}>
								<motion.h2 className="mb-6 text-center text-sm font-semibold tracking-[0.3em]" {...fadeUp}>
									POPULAR DESTINATIONS
								</motion.h2>
								<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
									{popularDestinations.map((item, index) => (
										<motion.article
											key={item.title}
											className="space-y-2"
											whileHover={{ y: -6, scale: 1.01 }}
											transition={{ duration: 0.2 }}
										>
											<img src={item.image} alt={item.title} className={classNames('w-full object-cover', index % 2 === 0 ? 'h-56' : 'h-56 md:h-80')} />
											<p className="text-sm font-semibold">{item.title}</p>
											<p className="text-xs text-slate-500">{item.subtitle}</p>
										</motion.article>
									))}
								</div>
							</motion.section>

							<motion.section className="px-6 pb-10 md:px-10" {...fadeUp}>
								<motion.h2 className="mb-6 text-center text-sm font-semibold tracking-[0.3em]" {...fadeUp}>
									FEATURED YACHTS
								</motion.h2>
								<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
									{featuredYachts.map((image, index) => (
										<motion.article
											key={`${image}-${index}`}
											className="space-y-2"
											whileHover={{ y: -6, scale: 1.01 }}
											transition={{ duration: 0.2 }}
										>
											<img src={image} alt="Featured yacht" className={classNames('w-full object-cover', index % 2 === 0 ? 'h-64' : 'h-64 md:h-80')} />
											<p className="text-xs text-slate-500">Luxury Collection</p>
										</motion.article>
									))}
								</div>
							</motion.section>

							<motion.section className="px-0" {...fadeUp}>
								<motion.h2 className="mb-6 text-center text-sm font-semibold tracking-[0.3em]" {...fadeUp}>MEMORIES</motion.h2>
								<div className="grid grid-cols-1 md:grid-cols-3">
									{memories.map((image, index) => (
										<motion.img
											key={`${image}-${index}`}
											src={image}
											alt="Travel memory"
											className="h-72 w-full object-cover"
											whileHover={{ scale: 1.02 }}
											transition={{ duration: 0.25 }}
										/>
									))}
								</div>
							</motion.section>
							<motion.div className='w-full h-[500px] relative text-white' {...fadeUp}>
								<img src="/images/moonlight/pexels-haris-khan-73724010-34113395.png" alt="World Tour" className="w-full h-full object-cover" />
								<div className='absolute top-20 left-0 md:left-20 right-0 p-6'>
									<h4 className='text-6xl font-light'><img src="/images/moonlight/logo-before-footer.svg" alt="Moonlight" className="h-16 md:h-28" /></h4>
									<p className='text-2xl font-light mt-2 pl-6 md:pl-16'>Yacht to your hearts deepest desire</p>
								</div>
							</motion.div>
						</>
					)}
					<motion.section className="bg-[#F7F7F7] px-6 py-14 md:px-10 dark:bg-[#0B1B1B]" {...fadeUp}>
						<div className="mx-auto container">
							{!selectedStory ? (
								<>
									<h2 className="text-center text-3xl font-semibold tracking-[0.2em] text-[#111827] dark:text-white">
										LATEST STORIES
									</h2>
									<p className="mt-3 text-center text-sm text-[#6b7280] dark:text-[#9CA3AF]">
										Insights, inspiration, and guides for your next yacht adventure
									</p>
									<div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
										{latestStories.map((story) => (
											<motion.article
												key={story.title}
												className="rounded-xl cursor-pointer"
												onClick={() => openStoryInPage(story.slug)}
												whileHover={{ y: -6 }}
												transition={{ duration: 0.2 }}
											>
												<div className="relative overflow-hidden rounded-xl">
													<img
														src={story.image}
														alt={story.title}
														className="h-80 w-full object-cover"
													/>
													<span className="absolute left-3 top-3 rounded-full bg-white px-3 py-1 text-[10px] font-semibold text-[#111827]">
														{story.tag}
													</span>
												</div>
												<p className="mt-4 text-xs text-[#6b7280] dark:text-[#9CA3AF]">{story.meta}</p>
												<h3 className="mt-2 text-xl font-semibold text-[#111827] dark:text-white">
													{story.title}
												</h3>
												<p className="mt-2 text-sm text-[#4b5563] dark:text-[#E5E7EB]">{story.description}</p>
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														openStoryInPage(story.slug);
													}}
													className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#012728] hover:opacity-80 dark:text-white"
												>
													Read More
													<span aria-hidden="true">›</span>
												</button>
											</motion.article>
										))}
									</div>
								</>
							) : (
								<>
									<header className="mb-8 flex items-start justify-between bg-[#00333A] px-6 py-5 text-white md:px-10 dark:bg-[#012B2E]">
										<div className="flex items-center">
											<NavLink to="/">
												<img src="/images/moonlight/logo.svg" alt="Moonlight" className="h-8" />
											</NavLink>
										</div>
										<nav className="hidden text-sm leading-6 md:block">
											<p>Destinations</p>
											<p>Yachts</p>
											<p>Tours</p>
											<p>Blogs</p>
										</nav>
										<div className="flex items-center gap-10 text-sm">
											<span>Contact</span>
											<span>EN</span>
										</div>
									</header>
									<article className="mx-auto max-w-[1200px]">

										<div className="mx-auto max-w-[900px]">
											<button
												type="button"
												onClick={() => navigate(`/chat${location.search}`)}
												className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-[#111827] hover:opacity-80 dark:text-white"
											>
												<span aria-hidden="true">‹</span>
												Back to stories
											</button>
											<div className="overflow-hidden rounded-xl bg-white shadow-sm dark:bg-[#0B1B1B]">
												<div className="relative">
													<img
														src={selectedStory.image}
														alt={selectedStory.title}
														className="h-[260px] w-full object-cover md:h-[330px]"
													/>
													<div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
													<div className="absolute bottom-6 left-6 right-6 text-white">
														<span className="rounded-full bg-white px-3 py-1 text-[10px] font-semibold text-[#111827]">
															{selectedStory.tag}
														</span>
														<h3 className="mt-3 max-w-3xl text-3xl font-semibold leading-tight">
															{selectedStory.title}
														</h3>
													</div>
												</div>
												<div className="px-6 py-7 md:px-10">
													<p className="mb-6 text-xs text-[#6b7280] dark:text-[#9CA3AF]">{selectedStory.meta}</p>
													<div className="space-y-5 text-sm leading-7 text-[#374151] dark:text-[#E5E7EB]">
														<p>
															There&apos;s a different side of Manama—one that reveals itself only when you leave the shore behind. Explore the city from a unique perspective aboard a luxury yacht.

														</p>
														<p>
															As your yacht glides across the calm waters of the Arabian Gulf, the city begins to unfold in a way that no street-level itinerary can offer. The skyline softens, the pace slows, and every landmark becomes part of a curated, panoramic experience.
														</p>
														<p>
															Whether you&apos;re planning a short escape or a full-day charter, exploring Manama by yacht transforms familiar attractions into something far more memorable.
														</p>
														<h4 className="text-xl font-semibold text-[#111827] dark:text-white">
															A Journey That Begins at the Water&apos;s Edge
														</h4>
														<p>
															Your experience starts just minutes away from Ramada by Wyndham Manama City Centre, where modern marinas meet the open sea. As you step aboard, the atmosphere shifts—this is no longer just sightseeing; it&apos;s a private, immersive journey.
														</p>
														<p>
															The gentle movement of the yacht, the sea breeze, and uninterrupted views set the tone for what&apos;s ahead.
														</p>
														<h4 className="text-xl font-semibold text-[#111827] dark:text-white">
															Landmarks, Reimagined from the Sea
														</h4>
														<p>
															From the deck, Manama&apos;s most iconic destinations take on a completely new character:
														</p>
														<h4 className="text-xl font-semibold text-[#111827] dark:text-white">
															Al Seef Mall
														</h4>
														<p>
															From the water, the bustling shopping district appears calm and architectural—its modern design standing in contrast to the natural blues surrounding it. Anchor nearby and transition from luxury cruising to world-class retail and dining within minutes.
														</p>
														<h4 className="text-xl font-semibold text-[#111827] dark:text-white">
															Frequently Asked Questions
														</h4>
														<p><strong>Is a yacht tour suitable for short stays?</strong><br />Absolutely. Even a few hours on the water offers a unique perspective of the city and its landmarks.</p>
														<p><strong>Do yacht routes include major attractions?</strong><br />Yes, routes can be tailored to pass by key locations such as Bahrain National Museum and the surrounding coastal highlights.</p>
														<p><strong>What&apos;s the best time to go?</strong><br />Late afternoon into sunset is ideal, offering cooler temperatures and stunning views as the city lights begin to appear.</p>
													</div>
													<div className="mt-8 rounded-xl bg-[#00330F] px-6 py-6 text-white">
														<h5 className="text-xl font-semibold">Ready to Experience Manama by Yacht?</h5>
														<p className="mt-2 text-sm text-white/80">
															Book your next voyage and discover the city from a perspective few ever see.
														</p>
														<button
															type="button"
															className="mt-4 rounded-full bg-white px-6 py-2 text-sm font-semibold text-[#00330F]"
														>
															Book Your Charter
														</button>
													</div>
												</div>
											</div>
										</div>
									</article>
								</>
							)}
						</div>
					</motion.section>
					<motion.footer className="border-t border-[#e5e7eb] bg-white px-6 py-12 md:px-10 dark:border-[#2B2B2B] dark:bg-[#0B1B1B]" {...fadeUp}>
						<div className="grid grid-cols-1 gap-10 md:grid-cols-3">
							<div>
								<h3 className="text-3xl font-semibold text-[#111827] dark:text-white">For Enquiry</h3>
								<p className="mt-3 max-w-[260px] text-sm text-[#4b5563] dark:text-[#E5E7EB]">
									Book your trip in a minute, experience once in a lifetime.
								</p>
								<div className="mt-10 max-w-[280px] border-b border-[#9ca3af] pb-1">
									<div className="flex items-center justify-between text-sm text-[#6b7280] dark:text-[#9CA3AF]">
										<span>enter your email</span>
										<button type="button" className="text-[#111827] dark:text-white">
											ok
										</button>
									</div>
								</div>
								<p className="mt-3 text-[11px] text-[#9ca3af] dark:text-[#9CA3AF]">
									By clicking ok, you agree to receive our newsletter.
								</p>
							</div>

							<div className="grid grid-cols-2 gap-8 text-[#111827] dark:text-white">
								<div>
									<h4 className="text-sm font-semibold">Information</h4>
									<div className="mt-3 space-y-1.5 text-xs text-[#4b5563] dark:text-[#E5E7EB]">
										<p>Destination tours</p>
										<p>Yachts</p>
										<p>Tours</p>
										<p>Legal Policy</p>
										<p>Offer</p>
									</div>
								</div>
								<div>
									<h4 className="text-sm font-semibold">Contacts</h4>
									<div className="mt-3 space-y-1.5 text-xs text-[#4b5563] dark:text-[#E5E7EB]">
										<p>moonlight@email.com</p>
										<p>+1 123 2345 699</p>
									</div>
								</div>
								<div className="col-span-2 mt-4 flex items-center gap-8 text-xs text-[#374151] dark:text-[#E5E7EB]">
									<span>Whatsapp</span>
									<span>Instagram</span>
									<span>Telegram</span>
								</div>
							</div>

							<div className="flex justify-start md:justify-end">
								<div className="text-left md:text-right">
									<p className="text-4xl font-light text-[#111827] dark:text-white">
										<img src="/images/moonlight/logo.svg" alt="Moonlight" className="h-10 brightness-0" />
									</p>
									<p className="mt-2 text-xs text-[#6b7280] dark:text-[#9CA3AF]">Yacht to your heart&apos;s deepest desire</p>
								</div>
							</div>
						</div>
					</motion.footer>
				</div>
			)}
			<AnimatePresence>
				{open && !embedOnly && (
					<motion.button
						type="button"
						aria-label="Close chat overlay"
						onClick={() => setOpen(false)}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="fixed inset-0 z-30 bg-black/35 backdrop-blur-[1px]"
					/>
				)}
			</AnimatePresence>
			<ChatDrawer
				isOpen={open}
				triggerId="hotelworld-chat-widget"
				historyId={undefined}
				action={undefined}
				chatData={socketMessages}
				variant="widget"
				agentId={scriptAgentId}
				clientId={widgetClientId}
				onSendSocketMessage={onSendSocketMessage}
				onClose={() => setOpen(false)}
			/>

			<AnimatePresence>
				{showHint && !open && (
					<motion.div
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 8 }}
						transition={{ duration: 0.3 }}
						className="fixed bottom-24 right-6 z-50 bg-gray-100 rounded-2xl px-4 py-2 text-sm text-black font-normal shadow"
					>
						{chatHintText}
					</motion.div>
				)}
			</AnimatePresence>
			<button
				type="button"
				onClick={toggleChat}
				className={`fixed bottom-6 right-6 z-50 items-center gap-3 rounded-full bg-transparent text-xs font-medium md:pl-28 ${open ? 'hidden' : 'flex'}`}
			>
				<span className={`mr-2 rounded-full py-1 text-[13px] text-white font-normal ${open ? 'hidden' : 'flex'}`}>
					Powered by HotelWorld AI
				</span>
				<span
					className={`h-[60px] w-[60px] items-center justify-center rounded-full bg-white shadow-xl transition-all md:flex  ${open ? 'hidden' : 'bg-white rounded-br-md flex'
						}`}
				>
					{open ? (
						<img
							src="/svg/icons/toggle-chat.svg"
							alt="Close chat"
							className="h-10 w-10"
						/>
					) : (
						<img
							src={toggleAvatar}
							alt="Open chat"
							className="h-10 w-10 rounded-full border border-black object-cover bg-white"
						/>
					)}
				</span>
			</button>
		</div>
	);
};

export default ChatPage;

